import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, FolderOpen, Loader2, User, X } from 'lucide-react';

interface CompanyProject {
  id: string;
  name: string;
  description: string | null;
  tech_stack: string[] | null;
  start_date: string | null;
  end_date: string | null;
  candidate_id: string;
  candidate: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  client_manager: {
    id: string;
    name: string;
    designation: string | null;
  } | null;
}

interface CompanyProjectsTabProps {
  companyId: string;
}

const CompanyProjectsTab = ({ companyId }: CompanyProjectsTabProps) => {
  const [projects, setProjects] = useState<CompanyProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<CompanyProject | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [techStack, setTechStack] = useState<string[]>([]);
  const [techInput, setTechInput] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [managerId, setManagerId] = useState('');
  
  // Dropdown data
  const [candidates, setCandidates] = useState<{ id: string; first_name: string | null; last_name: string | null }[]>([]);
  const [managers, setManagers] = useState<{ id: string; name: string; designation: string | null }[]>([]);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
    fetchCandidates();
    fetchManagers();
  }, [companyId]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidate_projects')
        .select(`
          id, name, description, tech_stack, start_date, end_date, candidate_id,
          candidate:candidate_id (id, first_name, last_name),
          client_manager:client_manager_id (id, name, designation)
        `)
        .eq('client_company_id', companyId)
        .order('start_date', { ascending: false });

      if (error) throw error;
      
      const transformed = (data || []).map(p => ({
        ...p,
        candidate: Array.isArray(p.candidate) ? p.candidate[0] : p.candidate,
        client_manager: Array.isArray(p.client_manager) ? p.client_manager[0] : p.client_manager,
      }));
      
      setProjects(transformed as CompanyProject[]);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    const { data } = await supabase
      .from('candidates')
      .select('id, first_name, last_name')
      .order('first_name');
    setCandidates(data || []);
  };

  const fetchManagers = async () => {
    const { data } = await supabase
      .from('company_people')
      .select('id, name, designation')
      .eq('company_id', companyId)
      .order('name');
    setManagers(data || []);
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setTechStack([]);
    setTechInput('');
    setCandidateId('');
    setManagerId('');
  };

  const openAddForm = () => {
    resetForm();
    setEditingProject(null);
    setFormOpen(true);
  };

  const openEditForm = (project: CompanyProject) => {
    setEditingProject(project);
    setName(project.name);
    setDescription(project.description || '');
    setStartDate(project.start_date || '');
    setEndDate(project.end_date || '');
    setTechStack(project.tech_stack || []);
    setCandidateId(project.candidate_id);
    setManagerId(project.client_manager?.id || '');
    setFormOpen(true);
  };

  const addTech = () => {
    const tech = techInput.trim();
    if (tech && !techStack.includes(tech)) {
      setTechStack([...techStack, tech]);
      setTechInput('');
    }
  };

  const removeTech = (tech: string) => {
    setTechStack(techStack.filter(t => t !== tech));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: 'Project name is required', variant: 'destructive' });
      return;
    }
    if (!candidateId) {
      toast({ title: 'Please select a candidate', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const projectData = {
        name: name.trim(),
        description: description.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        tech_stack: techStack.length > 0 ? techStack : null,
        candidate_id: candidateId,
        client_company_id: companyId,
        client_manager_id: managerId || null,
      };

      if (editingProject) {
        const { error } = await supabase
          .from('candidate_projects')
          .update(projectData)
          .eq('id', editingProject.id);
        if (error) throw error;
        toast({ title: 'Project updated' });
      } else {
        const { error } = await supabase
          .from('candidate_projects')
          .insert(projectData);
        if (error) throw error;
        toast({ title: 'Project added' });
      }

      setFormOpen(false);
      fetchProjects();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      const { error } = await supabase
        .from('candidate_projects')
        .delete()
        .eq('id', projectToDelete);

      if (error) throw error;
      toast({ title: 'Project deleted' });
      fetchProjects();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAddForm}>
          <Plus className="h-4 w-4 mr-2" />
          Add Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <Card className="p-8 text-center">
          <FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No projects found for this company</p>
          <Button variant="outline" className="mt-4" onClick={openAddForm}>
            <Plus className="h-4 w-4 mr-1" />
            Add First Project
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3">
          {projects.map((project) => (
            <Card key={project.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h5 className="font-semibold">{project.name}</h5>
                    {project.candidate && (
                      <Link 
                        to="/recruiter/candidates" 
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <User className="h-3 w-3" />
                        {project.candidate.first_name} {project.candidate.last_name}
                      </Link>
                    )}
                  </div>
                  {(project.start_date || project.end_date) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {project.start_date} - {project.end_date || 'Present'}
                    </p>
                  )}
                  {project.description && (
                    <p className="text-sm text-muted-foreground mt-2">{project.description}</p>
                  )}
                  {project.tech_stack && project.tech_stack.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {project.tech_stack.map((tech, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {project.client_manager && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      Manager: <span className="font-medium text-foreground">{project.client_manager.name}</span>
                      {project.client_manager.designation && ` (${project.client_manager.designation})`}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditForm(project)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => {
                      setProjectToDelete(project.id);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Add Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" />
            </div>
            <div>
              <Label>Candidate *</Label>
              <Select value={candidateId} onValueChange={setCandidateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select candidate..." />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Project description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Tech Stack</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={techInput}
                  onChange={(e) => setTechInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())}
                  placeholder="Add technology..."
                />
                <Button type="button" variant="secondary" onClick={addTech}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {techStack.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {techStack.map((tech, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {tech}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={() => removeTech(tech)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Project Manager</Label>
              <Select value={managerId || 'none'} onValueChange={(v) => setManagerId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select manager..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No manager</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} {m.designation && `(${m.designation})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingProject ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this project? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyProjectsTab;
